/**
 * Placements list page.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePlacements, useCreatePlacement } from '@/hooks/usePlacements';
import { useDomains } from '@/hooks/useDomains';
import { DataTable } from '@/components/tables/data-table';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PlacementForm, type PlacementFormData } from '@/components/forms/placement-form';
import { LoadingSpinner } from '@/components/ui/loading';
import { Plus, Copy, ExternalLink, Link as LinkIcon } from 'lucide-react';
import type { PlacementResponse } from '@/api/schemas';
import type { Column } from '@/components/tables/data-table';
import { cn } from '@/utils/cn';
import './list.scss';

export function PlacementsList() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const { data, isLoading } = usePlacements({ q: searchQuery || undefined });
  const { data: domains } = useDomains();
  const createPlacement = useCreatePlacement();

  const handleCreatePlacement = async (formData: PlacementFormData) => {
    try {
      setCreateError(null);
      await createPlacement.mutateAsync(formData);
      setShowCreateModal(false);
      setCreateError(null);
    } catch (error) {
      // Extract error message
      const errorMessage = error instanceof Error ? error.message : 'Failed to create placement. Please try again.';
      setCreateError(errorMessage);
    }
  };

  const handleOpenModal = () => {
    setCreateError(null);
    setShowCreateModal(true);
  };

  const handleCloseModal = () => {
    setCreateError(null);
    setShowCreateModal(false);
  };

  const handleCopyLink = (link: string) => {
    navigator.clipboard.writeText(link);
  };

  const columns: Column<PlacementResponse>[] = [
    {
      key: 'destination_url',
      header: 'Destination URL',
      render: (placement) => (
        <a
          href={placement.destination_url}
          target="_blank"
          rel="noopener noreferrer"
          className="placements-list__link"
          onClick={(e) => e.stopPropagation()}
        >
          <span>{placement.destination_url}</span>
          <ExternalLink />
        </a>
      ),
    },
    {
      key: 'placement_id',
      header: 'Tracking Link',
      render: (placement) => {
        // TODO: Use domain.domain_name from placement relationship instead of hardcoding
        // For now, using default domain - should be fetched from domain_id
        const trackingLink = `https://${placement.placement_id}.origynflow.com`;
        return (
          <div className="placements-list__tracking-link">
            <span className="code">{trackingLink}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCopyLink(trackingLink);
              }}
              className="placements-list__tracking-link-copy"
              title="Copy link"
            >
              <Copy />
            </button>
          </div>
        );
      },
    },
    {
      key: 'active',
      header: 'Status',
      render: (placement) => (
        <span className={cn('badge', placement.active ? 'badge--success' : 'badge--neutral')}>
          {placement.active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      render: (placement) => new Date(placement.created_at).toLocaleDateString(),
    },
  ];

  // Filter to only verified domains (required for placement creation)
  const verifiedDomains = domains?.domains.filter((domain) => domain.status === 'verified') || [];

  return (
    <div className="placements-list">
      <div className="placements-list__header">
        <div>
          <h1 className="h1">Placements</h1>
          <p className="subtitle">Manage your link placements</p>
        </div>
        <Button onClick={handleOpenModal}>
          <Plus style={{ marginRight: 'var(--of-space-2)', width: '1rem', height: '1rem' }} />
          Create Placement
        </Button>
      </div>

      <Card>
        <div className="placements-list__search">
          <input
            type="text"
            placeholder="Search placements..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input"
          />
        </div>
        <DataTable
          columns={columns}
          data={data?.placements || []}
          isLoading={isLoading}
          onRowClick={(placement) => {
            window.location.href = `/placements/${placement.placement_id}`;
          }}
        />
      </Card>

      {showCreateModal && (
        <div className="placements-list__modal">
          <div className="placements-list__modal-content">
            <h2 className="h3" style={{ marginBottom: 'var(--of-space-4)' }}>Create Placement</h2>
            {verifiedDomains.length > 0 ? (
              <PlacementForm
                domains={verifiedDomains}
                onSubmit={handleCreatePlacement}
                onCancel={handleCloseModal}
                isLoading={createPlacement.isPending}
                error={createError}
              />
            ) : domains && domains.domains.length > 0 ? (
              <div className="placements-list__modal-empty">
                <p>No verified domains available. Please verify a domain first.</p>
                <Link to="/domains">
                  <Button variant="primary">Go to Domains</Button>
                </Link>
              </div>
            ) : (
              <div className="placements-list__modal-empty">
                <p>No domains available. Please add a domain first.</p>
                <Link to="/domains">
                  <Button variant="primary">Add Domain</Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

